import type { DocumentRetrieval } from '../api.js';
import { timestamp } from '../format.js';

/**
 * WHAT LEFT THIS RECORD — the reader half of attributable retrieval (canon R4).
 *
 * The ledger has been written since #664 and read by nothing since. That is the gap this closes: an
 * audit row nobody can see is a database table, not accountability, and R4's promise — _"employees
 * retrieve files through the PEE, and retrieval is attributable"_ — is the whole argument for locking
 * the drive (R11/P6). Until this panel, the honest answer to "who has had this file" was a SQL prompt.
 *
 * It is deliberately the OUT half of a pair. `Documents` above says what came in and who put it there;
 * this says what went out and who took it — THROUGH THE HUB.
 *
 * 🔴 ADR-0052 D2: it is NOT the record's full traffic, and it must not read as if it were. Brad ruled
 * that a retrieval through the Nexus Deal Room lands on NEXUS'S ledger, not ours, so "who has had this
 * file" has two answers. This panel used to answer an empty ledger with "No one has retrieved a document
 * from this record" — a claim about every channel, made by a ledger that sees one. It now names its
 * boundary on every render, empty or not, until the retrieval-event feed asked of Nexus exists.
 */
export function RetrievalLog({
  retrievals,
  truncated,
}: {
  retrievals: DocumentRetrieval[];
  /** Older handovers exist beyond this page. Disclosed, never silently dropped. */
  truncated: boolean;
}) {
  return (
    <section className="card">
      <h3 className="card__title">Retrievals</h3>

      {retrievals.length === 0 ? (
        /*
          ⚠️ "Nobody has retrieved anything" and "we were not recording" are DIFFERENT claims, and an
          empty audit panel is read as the first. It is only true here because the ledger has recorded
          every handover since the route existed — so the panel may state it, and says which it means.
        */
        <p className="card__note">
          No one has retrieved a document from this record through the Hub. Every Hub retrieval is
          recorded here, with who took it.
        </p>
      ) : (
        <>
          <ol className="rlog">
            {retrievals.map((r) => (
              <li key={r.id} className="rlog__row">
                <span className="rlog__who">{actorLabel(r.retrievedByName)}</span>
                <span className="rlog__what">
                  {r.filename}{' '}
                  <span
                    className="rlog__file"
                    {...(r.servedSha256 ? { title: `sha256 ${r.servedSha256}` } : {})}
                  >
                    ({servedLabel(r)})
                  </span>
                </span>
                <span className="rlog__when">{timestamp(r.retrievedAt)}</span>
              </li>
            ))}
          </ol>
          {truncated && (
            // A truncated audit list that looks complete is worse than no list: a reader counts the
            // rows and concludes that is everything that happened.
            <p className="card__note">
              Showing the most recent {retrievals.length} retrievals. Older ones are on the record
              but not on this page.
            </p>
          )}
        </>
      )}
      <p className="card__note">
        Hub retrievals only. Files opened in the Nexus Deal Room are recorded by Nexus and do not
        appear here.
      </p>
    </section>
  );
}

/**
 * How an actor is named — and how an UNNAMED one is named.
 *
 * ⚠️ A row predating migration 0030 has no name because none was recorded, and there is no honest way
 * to recover it. "Unknown user" would assert we know a person and cannot identify them; the truth is
 * that we were not writing it down. Say that. Exported because the distinction is the point, and a
 * label this load-bearing should be pinned by a test rather than read off a screenshot.
 */
export function actorLabel(name: string | null): string {
  return name ?? 'Name not recorded';
}

/**
 * Which file a row handed over (ADR-0061 D4/D6).
 *
 * ⚠️ An unrecorded version says so. A row from before migration 0067, or from a Nexus that did not name
 * the file, must not read as "the current version": a document re-saved since then has a different
 * current version, and that is the confusion the column exists to end. Exported so the wording is pinned
 * by a test, like `actorLabel`.
 */
export function servedLabel(r: Pick<DocumentRetrieval, 'servedVersionNumber' | 'pinned'>): string {
  if (r.servedVersionNumber == null) return 'version not recorded';
  return r.pinned ? `v${r.servedVersionNumber}, as filed` : `v${r.servedVersionNumber}`;
}
